import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  useTheme,
  Divider,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import { useStores } from "../../../use-stores";
import AppSearchDropDown from "../../../../../views/components/common/filter/AppSearchDropDown";
import { ProductCategoriesMapping } from "../../../../../models/Products/Product";
import { GST_RATE_LIST } from "../../../../../models/gstRate";
import MediaAssetsUpload from "../../../../../views/components/common/upload/MediaAssetsUpload";
import ImageAPI from "../../../api/ImageAPI";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useNotification } from "../../../../../utils/NotificationsContext";

interface UrgentSourcingAddProductPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  categoriesList: ProductCategoriesMapping[];
  handleSaveNewCategory: () => void;
}
const UrgentSourcingAddProductPopup: React.FC<
  UrgentSourcingAddProductPopupProps
> = ({ open, onClose, onSave, categoriesList, handleSaveNewCategory }) => {
  const theme = useTheme();
  const { urgentSourcingStore } = useStores();
  const { showError } = useNotification();

  const isFormValid = () => {
    return (
      urgentSourcingStore.addProductFormData.productName &&
      urgentSourcingStore.addProductFormData.timeline &&
      urgentSourcingStore.addProductFormData.priceWithoutTax &&
      urgentSourcingStore.addProductFormData.fiftyQuantityPrice &&
      urgentSourcingStore.addProductFormData.gst &&
      urgentSourcingStore.addProductFormData.hsn &&
      urgentSourcingStore.addProductFormData.selectedCategory &&
      urgentSourcingStore.addProductFormData.description &&
      urgentSourcingStore.addProductFormData.vendorName &&
      urgentSourcingStore.addProductFormData.uploadedImageUrls &&
      urgentSourcingStore.addProductFormData.uploadedImageUrls.length > 0 &&
      urgentSourcingStore.addProductFormData.vendorPhoneNumber
    );
  };

  const handleSave = () => {
    urgentSourcingStore.setIsLoading(true);
    if (isFormValid()) {
      if (
        urgentSourcingStore.addProductFormData.uploadedImageUrls &&
        urgentSourcingStore.addProductFormData.uploadedImageUrls.length > 0
      ) {
        const imageAPI = new ImageAPI();
        imageAPI
          .uploadImage(
            urgentSourcingStore.addProductFormData.uploadedImageUrls,
            "product-images",
          )
          .then((uploadedImageUrls) => {
            urgentSourcingStore.updateAddProductField(
              "uploadedImagesURL",
              uploadedImageUrls,
            );
            onSave();
          })
          .catch((error) => {
            showError(
              "Error Occurred",
              error.response?.data?.message || error.message,
            );
          })
          .finally(() => {
            urgentSourcingStore.setIsLoading(false);
          });
      } else {
        onSave();
        urgentSourcingStore.setIsLoading(false);
      }
    } else {
      urgentSourcingStore.setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3">
            Urgent Sourcing Product
            <CustomTypography
              variant="heading7"
              color={theme.palette.customColors.greyTextColor}
              style={{ marginTop: "4px" }}
            >
              {" "}
              ( Request ID:{" "}
              {urgentSourcingStore.addProductFormData.urgentSourcingRequestId ||
                ""}{" "}
              / Lead ID:{" "}
              {urgentSourcingStore.addProductFormData.urgentSourcingRequestId
                ? urgentSourcingStore.urgentSourcingRaisedReqResponce.find(
                    (item) =>
                      item.id ===
                      Number(
                        urgentSourcingStore.addProductFormData
                          .urgentSourcingRequestId,
                      ),
                  )?.lead_id || ""
                : ""}
              )
            </CustomTypography>
          </CustomTypography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            scrollBehavior: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Product Name{" "}
                <span
                  style={{ color: theme.palette.error.main, marginLeft: 4 }}
                >
                  *
                </span>
              </CustomTypography>
              <CustomTextField
                width="100%"
                placeholder="Enter product name"
                disabled={urgentSourcingStore.isAddingNewCategory}
                value={
                  urgentSourcingStore.addProductFormData.productName ||
                  urgentSourcingStore.urgentSourcingRaisedReqResponce.find(
                    (item) =>
                      item.id ===
                      Number(
                        urgentSourcingStore.addProductFormData
                          .urgentSourcingRequestId,
                      ),
                  )?.product_name ||
                  ""
                }
                onChange={(e) =>
                  urgentSourcingStore.updateAddProductField(
                    "productName",
                    e.target.value ||
                      urgentSourcingStore.addProductFormData.productName ||
                      "",
                  )
                }
              />
            </Box>

            <Box
              sx={{
                mb: 3,
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Timeline (In Days){" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTextField
                  width="100%"
                  placeholder="Enter timeline in days"
                  type="number"
                  allowNegativeValue={false}
                  disabled={urgentSourcingStore.isAddingNewCategory}
                  value={
                    urgentSourcingStore.addProductFormData.timeline ||
                    urgentSourcingStore.urgentSourcingRaisedReqResponce.find(
                      (item) =>
                        item.id ===
                        Number(
                          urgentSourcingStore.addProductFormData
                            .urgentSourcingRequestId,
                        ),
                    )?.timeline ||
                    ""
                  }
                  onChange={(e) =>
                    urgentSourcingStore.updateAddProductField(
                      "timeline",
                      e.target.value ||
                        urgentSourcingStore.addProductFormData.timeline ||
                        "",
                    )
                  }
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Price without Tax{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTextField
                  width="100%"
                  placeholder="Enter price without tax"
                  type="number"
                  allowNegativeValue={false}
                  disabled={urgentSourcingStore.isAddingNewCategory}
                  value={urgentSourcingStore.addProductFormData.priceWithoutTax}
                  onChange={(e) =>
                    urgentSourcingStore.updateAddProductField(
                      "priceWithoutTax",
                      e.target.value,
                    )
                  }
                />
              </Box>
            </Box>

            <Box
              sx={{
                mb: 3,
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  50 Qty Price without Tax{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTextField
                  width="100%"
                  placeholder="Enter 50 qty price without tax"
                  type="number"
                  allowNegativeValue={false}
                  disabled={urgentSourcingStore.isAddingNewCategory}
                  value={
                    urgentSourcingStore.addProductFormData.fiftyQuantityPrice
                  }
                  onChange={(e) =>
                    urgentSourcingStore.updateAddProductField(
                      "fiftyQuantityPrice",
                      e.target.value,
                    )
                  }
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  GST{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <AppDropDown
                  title="Enter GST %"
                  fontSize="0.7rem"
                  isMultiSelect={false}
                  disabled={urgentSourcingStore.isAddingNewCategory}
                  value={
                    GST_RATE_LIST.find(
                      (opt) =>
                        opt.id ===
                        (urgentSourcingStore.addProductFormData.gst?.toString() ??
                          "0"),
                    )?.id || GST_RATE_LIST[0].id
                  }
                  list={GST_RATE_LIST.map((option) => ({
                    id: option.id,
                    name: option.value,
                    value: option.value,
                  }))}
                  menuZIndex={10000}
                  onSingleSelectionChange={(id, name) =>
                    urgentSourcingStore.updateAddProductField("gst", id)
                  }
                />
              </Box>
            </Box>

            <Box
              sx={{
                mb: 3,
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Price with GST
                </CustomTypography>
                <CustomTextField
                  width="100%"
                  placeholder="Auto-calculated"
                  type="number"
                  allowNegativeValue={false}
                  value={urgentSourcingStore.addProductFormData.priceWithGST}
                  disabled
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  HSN (6 or 8 digit mandatory){" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTextField
                  width="100%"
                  placeholder="Enter 6 or 8 digit HSN code"
                  disabled={urgentSourcingStore.isAddingNewCategory}
                  value={urgentSourcingStore.addProductFormData.hsn}
                  onChange={(e) =>
                    urgentSourcingStore.updateAddProductField(
                      "hsn",
                      e.target.value,
                    )
                  }
                />
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Categories{" "}
                <span
                  style={{ color: theme.palette.error.main, marginLeft: 4 }}
                >
                  *
                </span>
              </CustomTypography>
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
              >
                <Box sx={{ flex: 1 }}>
                  <AppSearchDropDown
                    required
                    isMultiSelect={false}
                    title="Select Category"
                    disabled={urgentSourcingStore.isAddingNewCategory}
                    list={categoriesList.map((category) => ({
                      id: String(category.category_id),
                      value: category.category_name || "",
                    }))}
                    fontSize="14px"
                    local={true}
                    borderColor={theme.palette.customColors.greyBackground}
                    value={
                      urgentSourcingStore.addProductFormData.selectedCategory
                        ? [
                            {
                              id: String(
                                urgentSourcingStore.addProductFormData
                                  .selectedCategory,
                              ),
                              value:
                                categoriesList.find(
                                  (category) =>
                                    String(category.category_id) ===
                                    String(
                                      urgentSourcingStore.addProductFormData
                                        .selectedCategory,
                                    ),
                                )?.category_name || "",
                            },
                          ]
                        : []
                    }
                    handleSelect={(value) =>
                      urgentSourcingStore.updateAddProductField(
                        "selectedCategory",
                        value[0]?.id || "",
                      )
                    }
                  />
                </Box>
                {!urgentSourcingStore.isAddingNewCategory && (
                  <AppButton
                    onClick={urgentSourcingStore.handleAddNewCategory}
                    variant="primary"
                    label="Add New Category"
                  />
                )}
              </Box>

              {/* New Category Input Field */}
              {urgentSourcingStore.isAddingNewCategory && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    mb: 2,
                    p: 2,
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <CustomTextField
                      width="100%"
                      placeholder="Enter new category name"
                      value={urgentSourcingStore.newCategoryName}
                      onChange={(e) =>
                        urgentSourcingStore.setNewCategoryName(e.target.value)
                      }
                    />
                  </Box>
                  <AppButton
                    onClick={() => handleSaveNewCategory()}
                    variant="primary"
                    label="Save"
                    disabled={
                      !urgentSourcingStore.newCategoryName.trim() ||
                      urgentSourcingStore.isSavingCategory
                    }
                    customStyles={{
                      minWidth: "80px",
                    }}
                  />
                  <AppButton
                    onClick={urgentSourcingStore.handleCancelNewCategory}
                    variant="grayscale"
                    label="Cancel"
                    disabled={urgentSourcingStore.isSavingCategory}
                    customStyles={{
                      minWidth: "80px",
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Description{" "}
                <span
                  style={{ color: theme.palette.error.main, marginLeft: 4 }}
                >
                  *
                </span>
              </CustomTypography>
              <TextField
                multiline={true}
                rows={2}
                fullWidth
                placeholder="Enter detailed product description"
                disabled={urgentSourcingStore.isAddingNewCategory}
                value={urgentSourcingStore.addProductFormData.description}
                onChange={(e) =>
                  urgentSourcingStore.updateAddProductField(
                    "description",
                    e.target.value,
                  )
                }
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Images
                <span
                  style={{ color: theme.palette.error.main, marginLeft: 4 }}
                >
                  *
                </span>
              </CustomTypography>
            </Box>
            <Box
              sx={{
                opacity: urgentSourcingStore.isAddingNewCategory ? 0.5 : 1,
                pointerEvents: urgentSourcingStore.isAddingNewCategory
                  ? "none"
                  : "auto",
              }}
            >
              <MediaAssetsUpload
                key={`product-${urgentSourcingStore.addProductFormData.urgentSourcingRequestId}-images`}
                existingImageList={[]}
                newImageList={
                  urgentSourcingStore.addProductFormData.uploadedImageUrls || []
                }
                removeImage={(image) => {
                  // Extract the index from the id (format: "file-0", "file-1", etc.)
                  const fileIndex =
                    typeof image.id === "string" && image.id.startsWith("file-")
                      ? parseInt(image.id.replace("file-", ""), 10)
                      : Number(image.id);

                  if (fileIndex !== undefined && fileIndex >= 0) {
                    const updatedFiles =
                      urgentSourcingStore.addProductFormData.uploadedImageUrls?.filter(
                        (_: File, index: number) => index !== fileIndex,
                      ) || [];
                    urgentSourcingStore.updateUploadedImageUrls(updatedFiles);
                  }
                }}
                addImagesToBeUploaded={(files) => {
                  urgentSourcingStore.updateUploadedImageUrls([
                    ...(urgentSourcingStore.addProductFormData
                      .uploadedImageUrls || []),
                    ...files,
                  ]);
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Vendor Information */}
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading5"
                style={{
                  fontWeight: 600,
                  color: theme.palette.customColors.blackBorder,
                  marginBottom: 20,
                }}
              >
                Vendor Information
              </CustomTypography>

              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <CustomTypography
                    variant="heading6"
                    style={{ fontWeight: 600, marginBottom: 16 }}
                  >
                    Vendor Name{" "}
                    <span
                      style={{
                        color: theme.palette.error.main,
                        marginLeft: 4,
                      }}
                    >
                      *
                    </span>
                  </CustomTypography>
                  <CustomTextField
                    width="100%"
                    placeholder="Enter vendor name"
                    disabled={urgentSourcingStore.isAddingNewCategory}
                    value={urgentSourcingStore.addProductFormData.vendorName}
                    onChange={(e) =>
                      urgentSourcingStore.updateAddProductField(
                        "vendorName",
                        e.target.value,
                      )
                    }
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <CustomTypography
                    variant="heading6"
                    style={{ fontWeight: 600, marginBottom: 16 }}
                  >
                    Vendor Phone Number{" "}
                    <span
                      style={{
                        color: theme.palette.error.main,
                        marginLeft: 4,
                      }}
                    >
                      *
                    </span>
                  </CustomTypography>
                  <CustomTextField
                    width="100%"
                    type="number"
                    placeholder="Enter 10-digit phone number"
                    disabled={urgentSourcingStore.isAddingNewCategory}
                    value={
                      urgentSourcingStore.addProductFormData.vendorPhoneNumber
                    }
                    onChange={(e) =>
                      urgentSourcingStore.updateAddProductField(
                        "vendorPhoneNumber",
                        e.target.value,
                      )
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <AppButton
            label="Cancel"
            onClick={onClose}
            variant="grayscale"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            label="Add Product"
            onClick={() => handleSave()}
            variant="primary"
            disabled={!isFormValid() || urgentSourcingStore.isAddingNewCategory}
            customStyles={{
              backgroundColor:
                isFormValid() && !urgentSourcingStore.isAddingNewCategory
                  ? theme.palette.customColors.EmeraldGreen
                  : theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor:
                  isFormValid() && !urgentSourcingStore.isAddingNewCategory
                    ? theme.palette.customColors.greenDark
                    : theme.palette.customColors.greyDarkborder,
                transform:
                  isFormValid() && !urgentSourcingStore.isAddingNewCategory
                    ? "translateY(-1px)"
                    : "none",
                boxShadow:
                  isFormValid() && !urgentSourcingStore.isAddingNewCategory
                    ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                    : "none",
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                cursor: "not-allowed",
              },
            }}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(UrgentSourcingAddProductPopup);
